// <pfs-mfa-enroll>: generische Self-Service-MFA-Faktor-Verwaltung (PFS-558).
//
// Treibt die generischen pfs-auth-Endpoints /api/mfa/* (PFS-555):
//   GET    {apiBase}/api/mfa/factors                       — Liste
//   POST   {apiBase}/api/mfa/factors/{type}/enroll         — anlegen
//   POST   {apiBase}/api/mfa/factors/{factorId}/confirm    — bestätigen (TOTP)
//   DELETE {apiBase}/api/mfa/factors/{factorId}            — entfernen
//
// Auth: Cookie über den Host-Proxy (apiFetch credentials:include); der Host
// (mednet-main/BFF) injiziert server-seitig den User-Bearer. Die WC kennt
// KEINE Tokens — brand-agnostisch über --pfs-*-Tokens.
//
// Konfiguration — wie die Plattform-WCs (mednet-*): ambient-Werte ermittelt die
// WC SELBST via Context-Source, Verhalten kommt als data-*-Attribut:
//   apiBase           via readSourceSync("apiBase") (Katalog-Name by-convention,
//                     wie apiFetch). data-api-base ist optionaler Override.
//   data-factor-types Komma-Liste anbietbarer Typen. Default "totp,recovery".
// Texte sind vorerst hart (DE) — wie die übrigen WCs; i18n folgt plattformweit
// (pfs-content), nicht als WC-Sonderlocke.
//
// Zwei Modi:
//   Self-Service (Default) — Faktor-Liste + Anlegen/Bestätigen/Löschen gegen
//     {apiBase}/api/mfa/* (Bearer via Host-Proxy). Wie bisher.
//   Login-Zwangs-Enroll — wenn data-enroll-url gesetzt ist: keine Liste, kein
//     Löschen; die WC richtet SOFORT einen TOTP-Faktor ein (Bootstrap für einen
//     Tenant-MFA-Pflicht-User ohne Faktor, PFS-559) und feuert bei Erfolg
//     pfs:mfa-success → der Host schließt den Login ab/leitet weiter. Das
//     Enroll-Ticket hält der Host/BFF (injiziert es server-seitig wie den
//     Bearer) — die WC kennt es NICHT.
//
// Konfiguration (data-*):
//   data-enroll-url   Login-Mode: Host-Endpoint, der an /api/login/enroll
//                     weiterreicht (Ticket-Injection im Host). Gesetzt → Login-Mode.
//   data-verify-url   Login-Mode: Host-Endpoint → /api/login/enroll-verify.
//
// Events:
//   pfs:mfa-factors-changed  (bubbles, composed) — Self-Service: nach Anlegen/Bestätigen/Löschen.
//   pfs:mfa-success          (bubbles, composed) — Login-Mode: Faktor bestätigt; detail = Verify-Antwort.
//   pfs:mfa-error            (bubbles, composed) — Login-Mode: endgültiger Fehlschlag.

import { defineComponent } from "../../composition/define-component.js";
import "../badge/pfs-badge.js";
import "../loading-indicator/pfs-loading-indicator.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { BUTTON_CSS } from "../../styles/button.js";
import { CARD_CSS } from "../../styles/card.js";
import { apiFetch, ApiError } from "../../store/api-fetch.js";
import { confirmDialog } from "../confirm-dialog/pfs-confirm-dialog.js";
import qrcode from "qrcode-generator";

// qrSvg rendert eine otpauth://-URI als scanbares QR-SVG (für Authenticator-
// Apps). Eigene, lokal erzeugte Grafik — kein externer QR-Dienst.
function qrSvg(uri: string): string {
  try {
    const qr = qrcode(0, "M");
    qr.addData(uri);
    qr.make();
    return qr.createSvgTag({ cellSize: 5, margin: 2, scalable: true });
  } catch {
    return "";
  }
}

interface FactorView {
  id: string;
  type: string;
  status: string;
  label?: string;
  createdAt: string;
  lastUsedAt?: string;
}

interface EnrollResult {
  factorId: string;
  type: string;
  status: string;
  display: Record<string, unknown>;
}

type View =
  | { kind: "list" }
  | { kind: "totp-confirm"; factorId: string; otpauthUri: string; secret: string }
  | { kind: "recovery-show"; codes: string[] };

interface EnrollHost extends HTMLElement {
  _factors?: FactorView[];
  _view?: View;
  _loading?: boolean;
  _busy?: boolean; // async Aktion läuft (Button gesperrt + Spinner) — Plattform-Kanon
  _error?: string;
  // Login-Mode: Verify-Antwort, zwischengeparkt während die Backup-Codes
  // angezeigt werden — wird bei der Bestätigung als pfs:mfa-success-detail
  // gefeuert (PFS-559).
  _loginSuccess?: unknown;
}

const TYPE_NAMES: Record<string, string> = {
  totp: "Authenticator-App",
  recovery: "Recovery-Codes",
};

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; color: var(--pfs-color-on-surface); font-size: 0.9rem; }
${CARD_CSS}
.card { padding: 1.5rem; display: flex; flex-direction: column; gap: 1.1rem; }
h2 { margin: 0; font-size: 1.05rem; font-weight: 600; }
/* Login-Mode: zentrierte Auth-Karte. Voll-Breite-Elemente (Input/Button)
   behalten ihre Breite (text-align wirkt nur auf Inline/Text). */
.card.center { text-align: center; }
.muted { color: var(--pfs-color-on-surface-muted); }
.factors { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.5rem; }
.factor {
  display: flex; align-items: center; justify-content: space-between; gap: 1rem;
  border: 1px solid var(--pfs-color-outline);
  border-radius: var(--pfs-radius-sm, 0.375rem); padding: 0.6rem 0.8rem;
}
.factor .meta { min-width: 0; }
.factor .name { font-weight: 500; }
.factor .sub { font-size: 0.8rem; color: var(--pfs-color-on-surface-muted); }
.actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
/* Einzelner Button (z.B. Login-Mode "Bestätigen", Recovery-Ack) nimmt die
   volle Breite; bei mehreren Buttons (Abbrechen+Bestätigen) bleibt's normal. */
.actions .btn:only-child { flex: 1 1 100%; }
${BUTTON_CSS}
/* Busy-Spinner (Plattform-Kanon: async Aktion sichtbar machen). */
.spin { display:inline-block; width:0.9em; height:0.9em; margin-right:0.45em; vertical-align:-0.1em; border:2px solid currentColor; border-top-color:transparent; border-radius:50%; animation: pfs-spin 0.7s linear infinite; }
@keyframes pfs-spin { to { transform: rotate(360deg); } }
.error { color: var(--pfs-color-on-danger-container); font-size: 0.85rem; }
.code-input {
  font-family: ui-monospace, monospace; font-size: 1.35rem; line-height: normal; text-align: center;
  letter-spacing: 0.3em; text-indent: 0.3em;
  padding: 0.8rem 0.5rem 0.6rem; border: 1px solid var(--pfs-color-outline-strong);
  border-radius: var(--pfs-radius-sm, 0.375rem); width: 100%; box-sizing: border-box;
  background: var(--pfs-color-surface-sunken); color: var(--pfs-color-on-surface);
}
.code-input::placeholder { color: var(--pfs-color-on-surface-subtle); }
/* Focus identisch zur <pfs-login-form> (@pfs/design Foundation): border-accent
   + weicher Blush-Ring, kein harter Outline. */
.code-input:focus { outline: none; border-color: var(--pfs-color-accent); box-shadow: var(--pfs-shadow-focus-ring, 0 0 0 3px rgba(59,130,246,0.35)); }
.secret { font-family: ui-monospace, monospace; word-break: break-all; background: var(--pfs-color-surface-sunken); padding: 0.5rem; border-radius: var(--pfs-radius-sm, 0.375rem); }
/* qr-canvas = theme-INVARIANTES echtes Weiß (kein Surface-Token): der QR-Code
   braucht Scan-Kontrast, auch im Dark-Mode (PFS-732-Token statt rohem #fff). */
.qr { background: var(--pfs-color-qr-canvas); padding: 0.85rem; border-radius: var(--pfs-radius-md, 0.5rem); border: 1px solid var(--pfs-color-outline); box-shadow: var(--pfs-shadow-card, 0 1px 3px rgba(0,0,0,0.08)); display: inline-block; line-height: 0; align-self: center; }
.qr svg { width: 180px; height: 180px; display: block; }
details summary { cursor: pointer; font-size: 0.8rem; }
.codes { list-style: none; margin: 0; padding: 0.75rem; background: var(--pfs-color-surface-sunken); border-radius: var(--pfs-radius-sm, 0.375rem); display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.3rem; font-family: ui-monospace, monospace; }
.row { display: flex; gap: 0.5rem; align-items: center; }
/* Redesign (PFS-734 Pass 2, Spiegel pfs-mfa-settings): Header-Icon, Faktor-Icon, Warn-Box. */
.ehd { display: flex; align-items: center; gap: 0.6rem; }
.card.center .ehd { flex-direction: column; gap: 0.5rem; }
.ehd .eico { display: grid; place-items: center; height: 40px; width: 40px; border-radius: 0.7rem; flex: none; color: var(--pfs-color-primary); background: color-mix(in srgb, var(--pfs-color-primary) 14%, transparent); }
.ehd .eico svg { width: 21px; height: 21px; }
.factor .fi { display: grid; place-items: center; height: 38px; width: 38px; border-radius: 0.6rem; flex: none; color: var(--pfs-color-primary); background: var(--pfs-color-surface); border: 1px solid var(--pfs-color-outline); }
.factor .fi svg { width: 18px; height: 18px; }
.factor .fm { min-width: 0; display: flex; flex-direction: column; gap: 0.12rem; }
.factor .fa { margin-left: auto; display: flex; align-items: center; gap: 0.6rem; }
.warn { display: flex; gap: 0.55rem; align-items: flex-start; text-align: left; font-size: 0.83rem; color: var(--pfs-color-badge-fg); background: var(--pfs-color-badge-bg); border: 1px solid color-mix(in srgb, var(--pfs-color-badge-fg) 25%, transparent); border-radius: 0.7rem; padding: 0.7rem 0.85rem; }
/* Zeilen-Aktion = klickbarer Text statt solidem Button (nur Haupt-CTAs = .btn). */
/* Zeilen-Aktion Entfernen = kanonische .btn.danger.sm (PFS-731). */
/* Recovery: Bestätigungs-Checkbox gated „weiter" + Kopiert-Hinweis. */
.ackrow { display: flex; align-items: center; gap: 0.5rem; text-align: left; font-size: 0.85rem; color: var(--pfs-color-on-surface-muted); cursor: pointer; }
.ackrow input { width: 1rem; height: 1rem; accent-color: var(--pfs-color-primary); flex: none; }
.copied { font-size: 0.8rem; font-weight: 600; color: var(--pfs-color-on-success-container); }
.btn:disabled { opacity: 0.55; cursor: default; }
`;

// Inline-SVG-Icons (currentColor) — Header + Faktor-Typ (Spiegel pfs-mfa-settings).
const ICON_SHIELD = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path stroke-linecap="round" stroke-linejoin="round" d="M12 3l7 3v6c0 4.4-3 7.5-7 9-4-1.5-7-4.6-7-9V6l7-3z"/><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4"/></svg>`;
const ICON_PHONE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><rect x="6" y="3" width="12" height="18" rx="2"/><path stroke-linecap="round" d="M10 18h4"/></svg>`;
const ICON_KEY = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path stroke-linecap="round" stroke-linejoin="round" d="M15 7a4 4 0 10-3.9 4.9L7 16v3h3l.5-.5H13v-2h2l1.4-1.4A4 4 0 0015 7z"/></svg>`;
function typeIcon(type: string): string {
  return type === "recovery" ? ICON_KEY : ICON_PHONE;
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function factorTypes(host: HTMLElement): string[] {
  const raw = host.getAttribute("data-factor-types");
  if (raw === null) return ["totp", "recovery"];
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

function typeName(type: string): string {
  return TYPE_NAMES[type] ?? type;
}

defineComponent({
  tagName: "pfs-mfa-enroll",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const h = host as EnrollHost;
    if (!h._view) h._view = { kind: "list" };

    const types = factorTypes(host);
    // data-issuer: pro App konfigurierbarer Anzeige-Name in der Authenticator-
    // App (otpauth issuer). Leer → Service-Default in pfs-auth.
    const issuer = host.getAttribute("data-issuer") ?? "";
    // Login-Zwangs-Enroll-Mode: data-enroll-url/-verify-url zeigen auf Host-
    // Endpoints, die das Enroll-Ticket server-seitig injizieren (WC kennt es
    // nicht). Gesetzt → kein Faktor-Listen-/Lösch-UI, direkt TOTP-Setup.
    const enrollUrl = host.getAttribute("data-enroll-url");
    const verifyUrl = host.getAttribute("data-verify-url") ?? "";
    const loginMode = enrollUrl !== null && enrollUrl !== "";

    const setState = (patch: Partial<Pick<EnrollHost, "_factors" | "_view" | "_loading" | "_busy" | "_error">>): void => {
      Object.assign(h, patch);
      paint();
    };

    const loadFactors = async (): Promise<void> => {
      setState({ _loading: true, _error: undefined });
      try {
        const res = await apiFetch<{ factors: FactorView[] }>(`/api/mfa/factors`);
        setState({ _factors: res.factors ?? [], _loading: false, _busy: false });
      } catch (e) {
        setState({ _loading: false, _busy: false, _error: errMsg(e, "Faktoren konnten nicht geladen werden.") });
      }
    };

    const enroll = async (type: string): Promise<void> => {
      setState({ _busy: true, _error: undefined });
      try {
        // Login-Mode: an die Host-Enroll-URL (factor_type im Body, Ticket
        // injiziert der Host). Self-Service: type im Pfad, /api/mfa/*.
        const url = loginMode
          ? enrollUrl!
          : `/api/mfa/factors/${encodeURIComponent(type)}/enroll`;
        const reqBody: Record<string, unknown> = loginMode ? { factor_type: type } : {};
        if (issuer) reqBody.issuer = issuer;
        const res = await apiFetch<EnrollResult>(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(reqBody),
        });
        const d = res.display ?? {};
        if (type === "totp") {
          h._view = {
            kind: "totp-confirm",
            factorId: res.factorId,
            otpauthUri: String(d.otpauthUri ?? d.uri ?? ""),
            secret: String(d.secret ?? ""),
          };
          h._loading = false;
          h._busy = false;
          paint();
        } else if (Array.isArray(d.codes)) {
          h._busy = false;
          h._view = { kind: "recovery-show", codes: d.codes as string[] };
          paint();
          emitChanged();
        } else {
          await loadFactors();
        }
      } catch (e) {
        setState({ _loading: false, _busy: false, _error: errMsg(e, "Anlegen fehlgeschlagen.") });
      }
    };

    const confirmTotp = async (factorId: string, code: string): Promise<void> => {
      setState({ _busy: true, _error: undefined });
      if (loginMode) {
        // Login-Mode: an die Host-Verify-URL (factor_id + response, Ticket
        // injiziert der Host). Erfolg → Login wird host-seitig abgeschlossen,
        // wir feuern pfs:mfa-success → der Host leitet weiter.
        try {
          const verifyBody: Record<string, unknown> = { factor_id: factorId, response: code };
          // return_to (optional) durchreichen — der Host leitet nach Erfolg
          // dorthin (mirror <pfs-mfa-challenge>).
          const returnTo = host.getAttribute("data-return-to");
          if (returnTo) verifyBody.return_to = returnTo;
          const res = await apiFetch<{ recoveryCodes?: string[] }>(verifyUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(verifyBody),
          });
          // Zwangs-Enroll (PFS-559): wurden Backup-Codes erzeugt, erst anzeigen
          // (einmalig sichtbar) + Bestätigung abwarten, DANN Erfolg feuern.
          const codes = Array.isArray(res?.recoveryCodes) ? res.recoveryCodes : null;
          if (codes && codes.length) {
            h._loginSuccess = res;
            h._busy = false;
            h._view = { kind: "recovery-show", codes };
            paint();
            return;
          }
          host.dispatchEvent(new CustomEvent("pfs:mfa-success", { bubbles: true, composed: true, detail: res }));
        } catch (e) {
          const status = e instanceof ApiError ? e.status : 0;
          const detail = errMsg(e, "Bestätigung fehlgeschlagen — Code prüfen.");
          setState({ _busy: false, _error:detail });
          host.dispatchEvent(
            new CustomEvent("pfs:mfa-error", { bubbles: true, composed: true, detail: { status, detail } }),
          );
        }
        return;
      }
      try {
        await apiFetch(`/api/mfa/factors/${encodeURIComponent(factorId)}/confirm`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ response: code }),
        });
        h._view = { kind: "list" };
        emitChanged();
        await loadFactors();
      } catch (e) {
        setState({ _busy: false, _error:errMsg(e, "Bestätigung fehlgeschlagen — Code prüfen.") });
      }
    };

    const removeFactor = async (f: FactorView): Promise<void> => {
      const ok = await confirmDialog({
        title: "Faktor entfernen?",
        message: `Der Faktor „${f.label || typeName(f.type)}" wird entfernt. Ohne zweiten Faktor sinkt die Kontosicherheit.`,
        confirmLabel: "Entfernen",
        variant: "danger",
      });
      if (!ok) return;
      setState({ _busy: true, _error: undefined });
      try {
        await apiFetch(`/api/mfa/factors/${encodeURIComponent(f.id)}`, { method: "DELETE" });
        emitChanged();
        await loadFactors();
      } catch (e) {
        // Idempotent: 404 = Faktor bereits weg (Doppel-Delete) → kein Fehler.
        if (e instanceof ApiError && e.status === 404) {
          emitChanged();
          await loadFactors();
          return;
        }
        setState({ _busy: false, _error: errMsg(e, "Entfernen fehlgeschlagen.") });
      }
    };

    const emitChanged = (): void => {
      host.dispatchEvent(new CustomEvent("pfs:mfa-factors-changed", { bubbles: true, composed: true }));
    };

    function paint(): void {
      if (!root) return;
      let inner = "";
      const view = h._view ?? { kind: "list" };

      if (view.kind === "totp-confirm") {
        inner = `
          <div class="ehd"><span class="eico">${ICON_PHONE}</span><h2>Authenticator-App einrichten</h2></div>
          ${loginMode ? `<p class="muted">Dieser Zugang erfordert Zwei-Faktor-Authentifizierung. Jetzt eine Authenticator-App einrichten, um die Anmeldung abzuschließen.</p>` : ""}
          <p class="muted">QR-Code mit einer Authenticator-App scannen — oder den Schlüssel manuell eintragen — und mit dem aktuellen 6-stelligen Code bestätigen.</p>
          ${view.otpauthUri ? `<div class="qr" aria-label="QR-Code zum Scannen">${qrSvg(view.otpauthUri)}</div>` : ""}
          ${view.secret ? `<div class="secret">${esc(view.secret)}</div>` : ""}
          ${view.otpauthUri ? `<details><summary class="muted">otpauth-URI anzeigen</summary><div class="secret" data-otpauth>${esc(view.otpauthUri)}</div></details>` : ""}
          <input class="code-input" type="text" inputmode="numeric" autocomplete="one-time-code" placeholder="123456" data-confirm-code ${h._busy ? "disabled" : ""} />
          ${h._error ? `<p class="error">${esc(h._error)}</p>` : ""}
          <div class="actions">
            ${loginMode ? "" : `<button class="btn secondary" data-cancel ${h._busy ? "disabled" : ""}>Abbrechen</button>`}
            <button class="btn primary" data-do-confirm ${h._busy ? "disabled" : ""}>${h._busy ? `<span class="spin" aria-hidden="true"></span>Prüfe…` : "Aktivieren"}</button>
          </div>`;
      } else if (view.kind === "recovery-show") {
        inner = `
          <div class="ehd"><span class="eico">${ICON_KEY}</span><h2>Backup-Codes</h2></div>
          <div class="warn"><span aria-hidden="true">⚠</span><span>${loginMode ? "Die Authenticator-App ist eingerichtet. Diese Backup-Codes sichern — bei Verlust des Geräts ermöglichen sie weiterhin den Zugang. Sie werden nur jetzt angezeigt." : "Diese Einmal-Codes sicher aufbewahren — sie werden nur jetzt angezeigt. Jeder Code funktioniert genau einmal."}</span></div>
          <ul class="codes">${view.codes.map((c) => `<li>${esc(c)}</li>`).join("")}</ul>
          <div class="actions">
            <button class="btn secondary" data-download>Herunterladen</button>
            <button class="btn secondary" data-copy>Kopieren</button>
            <span class="copied" data-copied hidden>Kopiert ✓</span>
          </div>
          <label class="ackrow"><input type="checkbox" data-ack-check /> Ich habe die Backup-Codes sicher gespeichert.</label>
          <div class="actions">${loginMode ? "" : `<button class="btn secondary" data-back>Zurück</button>`}<button class="btn primary" data-ack disabled>${loginMode ? "Weiter" : "Fertig"}</button></div>`;
      } else if (loginMode) {
        // Login-Zwangs-Enroll: keine Liste/Add-Buttons — nur Vorbereitungs-
        // Hinweis bzw. (bei Fehler) ein Retry. Der Erfolgsweg ist totp-confirm.
        inner = `
          <div class="ehd"><span class="eico">${ICON_SHIELD}</span><h2>Zwei-Faktor-Authentifizierung einrichten</h2></div>
          ${h._loading ? `<pfs-loading-indicator data-variant="inline" data-text="Einrichtung wird vorbereitet"></pfs-loading-indicator>` : ""}
          ${h._error ? `<p class="error">${esc(h._error)}</p>` : ""}
          ${h._error ? `<div class="actions"><button class="btn primary" data-add="totp">Erneut versuchen</button></div>` : ""}`;
      } else {
        const factors = h._factors ?? [];
        const list = h._loading
          ? `<pfs-loading-indicator data-variant="inline" data-text="Faktoren werden geladen"></pfs-loading-indicator>`
          : factors.length === 0
            ? `<p class="muted">Noch kein zweiter Faktor eingerichtet.</p>`
            : `<ul class="factors">${factors.map(factorRow).join("")}</ul>`;
        const addButtons = types
          .map(
            (t, i) =>
              `<button class="btn ${i === 0 ? "primary" : "secondary"}" data-add="${esc(t)}" ${h._busy ? "disabled" : ""}>${h._busy ? `<span class="spin" aria-hidden="true"></span>` : ""}${esc(typeName(t))} hinzufügen</button>`,
          )
          .join("");
        inner = `
          <div class="ehd"><span class="eico">${ICON_SHIELD}</span><h2>Zwei-Faktor-Authentifizierung</h2></div>
          ${list}
          ${h._error ? `<p class="error">${esc(h._error)}</p>` : ""}
          <div class="actions">${addButtons}</div>`;
      }

      // Login-Mode = zentrierte Auth-Karte (kohärenter Login-Look); Self-Service
      // (Faktor-Liste) bleibt links-ausgerichtet.
      const cardClass = loginMode && view.kind !== "list" ? "card center" : "card";
      root.innerHTML = `<style>${STYLES}</style><div class="${cardClass}">${inner}</div>`;
      wire();
    }

    function factorRow(f: FactorView): string {
      const badgeVariant = f.status === "active" ? "success" : f.status === "pending" ? "warning" : "neutral";
      const sub = f.lastUsedAt ? `zuletzt genutzt ${esc(f.lastUsedAt)}` : `erstellt ${esc(f.createdAt)}`;
      return `
        <li class="factor">
          <span class="fi">${typeIcon(f.type)}</span>
          <span class="fm">
            <span class="name">${esc(f.label || typeName(f.type))}</span>
            <span class="sub">${esc(typeName(f.type))} · ${sub}</span>
          </span>
          <span class="fa"><pfs-badge data-variant="${badgeVariant}">${esc(f.status)}</pfs-badge><button class="btn danger sm" data-del="${esc(f.id)}">Entfernen</button></span>
        </li>`;
    }

    function wire(): void {
      if (!root) return;
      root.querySelectorAll<HTMLElement>("[data-add]").forEach((b) =>
        b.addEventListener("click", () => void enroll(b.getAttribute("data-add") ?? "")),
      );
      root.querySelectorAll<HTMLElement>("[data-del]").forEach((b) =>
        b.addEventListener("click", () => {
          const id = b.getAttribute("data-del") ?? "";
          const f = (h._factors ?? []).find((x) => x.id === id);
          if (f) void removeFactor(f);
        }),
      );
      root.querySelector("[data-cancel]")?.addEventListener("click", () => {
        h._view = { kind: "list" };
        h._error = undefined;
        paint();
      });
      const doConfirm = (): void => {
        const v = h._view;
        const input = root.querySelector<HTMLInputElement>("[data-confirm-code]");
        if (v && v.kind === "totp-confirm" && input) void confirmTotp(v.factorId, input.value.trim());
      };
      root.querySelector("[data-do-confirm]")?.addEventListener("click", doConfirm);
      // Enter im Code-Feld bestätigt — einheitlich mit <pfs-mfa-challenge>.
      root.querySelector<HTMLInputElement>("[data-confirm-code]")?.addEventListener("keydown", (e) => {
        if ((e as KeyboardEvent).key === "Enter") doConfirm();
      });
      root.querySelector("[data-ack]")?.addEventListener("click", () => {
        if (loginMode) {
          // Backup-Codes gesichert → jetzt erst Login abschließen.
          host.dispatchEvent(
            new CustomEvent("pfs:mfa-success", { bubbles: true, composed: true, detail: h._loginSuccess ?? {} }),
          );
          return;
        }
        h._view = { kind: "list" };
        void loadFactors();
      });
      // „Zurück" (nur Self-Service, immer aktiv) — sonst sitzt man in der
      // Backup-Code-Ansicht fest, bis die Codes bestätigt sind, und kommt nie
      // zur Faktoren-Liste / Authenticator-Einrichtung zurück. Im loginMode
      // bleibt der Gate: die Anmeldung MUSS hier abgeschlossen werden.
      root.querySelector("[data-back]")?.addEventListener("click", () => {
        h._view = { kind: "list" };
        void loadFactors();
      });
      // Backup-Codes: Herunterladen / Kopieren + Bestätigungs-Gate (der
      // Weiter-Button ist disabled, bis die Checkbox bestätigt ist).
      const recoveryCodesText = (): string => {
        const v = h._view;
        return v && v.kind === "recovery-show" ? v.codes.join("\n") : "";
      };
      root.querySelector("[data-download]")?.addEventListener("click", () => {
        const blob = new Blob([recoveryCodesText() + "\n"], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        // Dateiname von der nutzenden App konfigurierbar (data-download-filename);
        // neutraler Default statt „pfs-…".
        a.download = host.getAttribute("data-download-filename") || "backup-codes.txt";
        a.click();
        URL.revokeObjectURL(url);
      });
      root.querySelector("[data-copy]")?.addEventListener("click", () => {
        void navigator.clipboard?.writeText(recoveryCodesText());
        const c = root.querySelector<HTMLElement>("[data-copied]");
        if (c) c.hidden = false;
      });
      const ackCheck = root.querySelector<HTMLInputElement>("[data-ack-check]");
      const ackBtn = root.querySelector<HTMLButtonElement>("[data-ack]");
      ackCheck?.addEventListener("change", () => {
        if (ackBtn) ackBtn.disabled = !ackCheck.checked;
      });
    }

    if (loginMode) {
      // Zwangs-Enroll: keine Liste laden — direkt TOTP einrichten.
      h._loading = true;
      paint();
      void enroll("totp");
    } else {
      paint();
      void loadFactors();
    }
  },
});

function errMsg(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.detail ?? fallback;
  return fallback;
}
